import { selectSplitScreenSlice } from "@/store/slices/split-screen.slice";
import { Suspense, useEffect, useRef, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { Panel, Group, Separator } from "react-resizable-panels";
import { cn } from "@/utils/cn";
import { RouteFallback } from "@/components/layout/RouteFallback";

import { Outlet, useMatches } from "react-router-dom";
import {
  SplitScreenProvider,
  useSplitScreenContext,
} from "./split-screen-context";
import { createRoot, type Root } from "react-dom/client";
import { SplitBottomPanelApp } from "./split-bottom-panel-app";
import { SplitPanelEmptyState } from "./split-panel-empty-state";
import { getHighlightBorderStyles } from "./split-screen.constants";
import { splitScreenRoutes } from "@/routes/split-screen-routes";

const contentPadding = "p-1.5 md:p-2";

type LayoutHandle = {
  flushContent?: boolean;
};

export const PageContent = () => {
  const { showSplitScreen } = useSelector(selectSplitScreenSlice);
  const matches = useMatches();
  const isFlushContent = matches.some(
    (match) => (match.handle as LayoutHandle | undefined)?.flushContent,
  );
  const pagePadding = isFlushContent ? "p-0" : contentPadding;

  return (
    <SplitScreenProvider>
      <main
        className={cn(
          "flex flex-col flex-1 overflow-y-auto scrollbar-hidden",
          !showSplitScreen && pagePadding,
        )}
      >
        <SplitScreen showSplitScreen={showSplitScreen} contentClassName={pagePadding}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet context={{ split: true }} />
          </Suspense>
        </SplitScreen>
      </main>
    </SplitScreenProvider>
  );
};

export const SplitScreen = ({
  children,
  showSplitScreen,
  contentClassName,
}: {
  children: ReactNode;
  showSplitScreen: boolean;
  contentClassName: string;
}) => {
  const { splitPath, highlightColor, splitPortalRef } = useSplitScreenContext();

  // CREATING A BRAND NEW REACT INSTACE TO RUN SPLIT MODULE
  const bottomRootRef = useRef<Root | null>(null);

  useEffect(() => {
    if (!splitPath || !splitPortalRef.current) return;
    let isSubscribed = true;
    const timeoutId = setTimeout(() => {
      if (!isSubscribed) return;
      // Cleanup previous root
      if (bottomRootRef.current) {
        bottomRootRef.current.unmount();
        bottomRootRef.current = null;
      }
      const root = createRoot(splitPortalRef.current!);
      bottomRootRef.current = root;
      root.render(
        <SplitBottomPanelApp
          routes={splitScreenRoutes}
          initialPath={splitPath}
        />,
      );
    }, 0);

    return () => {
      isSubscribed = false;
      clearTimeout(timeoutId);
    };
  }, [splitPath]);

  if (!showSplitScreen) return children;
  return (
    <Group orientation="vertical">
      <Panel minSize={20} defaultSize="50%" className={cn(contentClassName)}>
        {children}
      </Panel>

      <Separator className="w-full h-0 relative">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="w-8 h-1.5 rounded-t-sxs"
            style={{ background: highlightColor }}
          />
        </div>
      </Separator>

      <Panel
        minSize={20}
        maxSize="600px"
        defaultSize="50%"
        className={cn(contentPadding, "rounded-t bg-white")}
        style={getHighlightBorderStyles(highlightColor)}
      >
        {splitPath ? (
          // NEW REACT PORTAL
          <div ref={splitPortalRef} style={{ height: "100%" }} />
        ) : (
          <SplitPanelEmptyState />
        )}
      </Panel>
    </Group>
  );
};
